import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import {
  Assessment,
  CheckCircle,
  Cancel,
  Publish,
  TrendingUp,
  People,
  EventAvailable,
  EventBusy,
  Visibility,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { resultAPI, examinationAPI, branchAPI, courseAPI, batchAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const ExamResults = () => {
  const { isSuperAdmin, user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedExamination, setSelectedExamination] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedResult, setSelectedResult] = useState('');
  const [publishFilter, setPublishFilter] = useState('');

  // Data
  const [examinations, setExaminations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [results, setResults] = useState([]);
  const [resultStats, setResultStats] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Dialog
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishingAll, setPublishingAll] = useState(false);
  const [detailsResult, setDetailsResult] = useState(null);

  useEffect(() => {
    fetchExaminations();
    if (isSuperAdmin) {
      fetchBranches();
    }
    fetchCourses();
    fetchBatches();
  }, []);

  useEffect(() => {
    setPage(0);
    if (selectedExamination) {
      if (activeTab === 0) {
        fetchAttendance();
      } else {
        fetchResults();
      }
    }
  }, [selectedExamination, selectedBranch, selectedCourse, selectedBatch, selectedResult, publishFilter, activeTab]);

  const fetchExaminations = async () => {
    try {
      const response = await examinationAPI.getAll();
      setExaminations(response.data.data || response.data.examinations || []);
    } catch (error) {
      console.error('Error fetching examinations:', error);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getAll({ limit: 1000 });
      setBranches(response.data.data || response.data.branches || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await courseAPI.getAll({ limit: 1000 });
      setCourses(response.data.data || response.data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await batchAPI.getAll();
      setBatches(response.data.data?.batches || []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedBranch) params.branch = selectedBranch;
      if (selectedCourse) params.course = selectedCourse;
      if (selectedBatch) params.batch = selectedBatch;

      const response = await resultAPI.getExamAttendance(selectedExamination, params);
      setAttendanceData(response.data.data?.attendance || []);
      setAttendanceStats(response.data.data?.stats);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedBranch) params.branch = selectedBranch;
      if (selectedCourse) params.course = selectedCourse;
      if (selectedBatch) params.batch = selectedBatch;
      if (selectedResult) params.result = selectedResult;
      if (publishFilter) params.isPublished = publishFilter === 'published';

      const response = await resultAPI.getExaminationResults(selectedExamination, params);
      setResults(response.data.data?.results || []);
      setResultStats(response.data.data?.stats);
    } catch (error) {
      console.error('Error fetching results:', error);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAllResults = async () => {
    try {
      setLoading(true);
      const response = await resultAPI.generateAllResults(selectedExamination);
      toast.success(`Generated ${response.data.data?.generated || 0} results successfully`);
      fetchResults();
    } catch (error) {
      console.error('Error generating results:', error);
      toast.error('Failed to generate results');
    } finally {
      setLoading(false);
    }
  };

  const handlePublishResult = async (resultId) => {
    try {
      await resultAPI.publishResult(resultId);
      toast.success('Result published successfully');
      fetchResults();
    } catch (error) {
      console.error('Error publishing result:', error);
      toast.error('Failed to publish result');
    }
  };

  const handlePublishAllResults = async () => {
    try {
      setPublishingAll(true);
      const response = await resultAPI.publishAllResults(selectedExamination);
      toast.success(`Published ${response.data.data?.published || 0} results successfully`);
      setShowPublishDialog(false);
      fetchResults();
    } catch (error) {
      console.error('Error publishing results:', error);
      toast.error('Failed to publish results');
    } finally {
      setPublishingAll(false);
    }
  };

  const clearFilters = () => {
    setSelectedBranch('');
    setSelectedCourse('');
    setSelectedBatch('');
    setSelectedResult('');
    setPublishFilter('');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Examination Results
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View attendance, generate and publish results
          </Typography>
        </Box>
      </Box>

      {/* Examination Selection */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Select Examination
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Examination</InputLabel>
              <Select
                value={selectedExamination}
                onChange={(e) => setSelectedExamination(e.target.value)}
                label="Examination"
              >
                <MenuItem value="">Select Examination</MenuItem>
                {examinations.map((exam) => (
                  <MenuItem key={exam._id} value={exam._id}>
                    {exam.name} ({exam.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {selectedExamination && (
        <>
          {/* Tabs */}
          <Paper elevation={2} sx={{ mb: 3 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Attendance" icon={<EventAvailable />} iconPosition="start" />
              <Tab label="Results" icon={<Assessment />} iconPosition="start" />
            </Tabs>
          </Paper>

          {/* Filters */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Filters
              </Typography>
              <Button size="small" onClick={clearFilters}>
                Clear Filters
              </Button>
            </Box>
            <Grid container spacing={2}>
              {isSuperAdmin && (
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Branch</InputLabel>
                    <Select
                      value={selectedBranch}
                      onChange={(e) => setSelectedBranch(e.target.value)}
                      label="Branch"
                      MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
                    >
                      <MenuItem value="">All Branches</MenuItem>
                      {branches.map((branch) => (
                        <MenuItem key={branch._id} value={branch._id}>
                          {branch.name} ({branch.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Course</InputLabel>
                  <Select
                    value={selectedCourse}
                    onChange={(e) => setSelectedCourse(e.target.value)}
                    label="Course"
                    MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
                  >
                    <MenuItem value="">All Courses</MenuItem>
                    {courses.map((course) => (
                      <MenuItem key={course._id} value={course._id}>
                        {course.name} ({course.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Batch</InputLabel>
                  <Select
                    value={selectedBatch}
                    onChange={(e) => setSelectedBatch(e.target.value)}
                    label="Batch"
                    MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
                  >
                    <MenuItem value="">All Batches</MenuItem>
                    {batches.map((b) => (
                      <MenuItem key={b._id} value={b._id}>
                        {b.name} ({b.year})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {activeTab === 1 && (
                <>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Result</InputLabel>
                      <Select
                        value={selectedResult}
                        onChange={(e) => setSelectedResult(e.target.value)}
                        label="Result"
                      >
                        <MenuItem value="">All Results</MenuItem>
                        <MenuItem value="pass">Pass</MenuItem>
                        <MenuItem value="fail">Fail</MenuItem>
                        <MenuItem value="absent">Absent</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Published Status</InputLabel>
                      <Select
                        value={publishFilter}
                        onChange={(e) => setPublishFilter(e.target.value)}
                        label="Published Status"
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="published">Published</MenuItem>
                        <MenuItem value="unpublished">Unpublished</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          {/* Attendance Tab */}
          {activeTab === 0 && (
            <>
              {/* Attendance Stats */}
              {attendanceStats && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <People sx={{ fontSize: 40, color: 'primary.main' }} />
                          <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                              {attendanceStats.total}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Total Students
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <EventAvailable sx={{ fontSize: 40, color: 'success.main' }} />
                          <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                              {attendanceStats.attended}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Attended
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <EventBusy sx={{ fontSize: 40, color: 'error.main' }} />
                          <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                              {attendanceStats.absent}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Absent
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <TrendingUp sx={{ fontSize: 40, color: 'warning.main' }} />
                          <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                              {attendanceStats.total > 0
                                ? Math.round((attendanceStats.attended / attendanceStats.total) * 100)
                                : 0}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Attendance Rate
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Attendance Table */}
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Student Attendance
                </Typography>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : attendanceData.length === 0 ? (
                  <Alert severity="info">
                    No students have been assigned to this examination yet. Please assign students to view attendance data.
                  </Alert>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Hall Ticket No.</strong></TableCell>
                          <TableCell><strong>Roll No.</strong></TableCell>
                          <TableCell><strong>Form Number</strong></TableCell>
                          <TableCell><strong>Name</strong></TableCell>
                          {isSuperAdmin && <TableCell><strong>Branch</strong></TableCell>}
                          <TableCell><strong>Course</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Time Spent</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {attendanceData.map((record) => (
                          <TableRow key={record.studentExam._id}>
                            <TableCell>{record.studentExam.hallTicketNumber || 'N/A'}</TableCell>
                            <TableCell>{record.studentExam.rollNumber || 'N/A'}</TableCell>
                            <TableCell>{record.student.formNumber || 'N/A'}</TableCell>
                            <TableCell>{record.student.name || 'N/A'}</TableCell>
                            {isSuperAdmin && <TableCell>{record.branch?.name || 'N/A'}</TableCell>}
                            <TableCell>{record.student.course?.name || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={record.attendanceStatus === 'attended' ? 'Attended' : record.attendanceStatus === 'in-progress' ? 'In Progress' : 'Absent'}
                                color={record.attendanceStatus === 'attended' ? 'success' : record.attendanceStatus === 'in-progress' ? 'warning' : 'error'}
                                size="small"
                                icon={record.attendanceStatus === 'attended' ? <CheckCircle /> : <Cancel />}
                              />
                            </TableCell>
                            <TableCell>
                              {record.submission?.timeSpent
                                ? `${Math.floor(record.submission.timeSpent / 60)} min`
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </>
          )}

          {/* Results Tab */}
          {activeTab === 1 && (
            <>
              {/* Result Stats */}
              {resultStats && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={2.4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          {resultStats.total}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total Results
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                          {resultStats.passed}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Passed
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>
                          {resultStats.failed}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Failed
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>
                          {resultStats.unpublished}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Unpublished
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {resultStats.averagePercentage}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Average
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Action Buttons - SuperAdmin Only */}
              {isSuperAdmin && (
                <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGenerateAllResults}
                    disabled={loading}
                  >
                    Generate All Results
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<Publish />}
                    onClick={() => setShowPublishDialog(true)}
                    disabled={loading || resultStats?.unpublished === 0}
                  >
                    Publish All Results
                  </Button>
                </Box>
              )}

              {/* Results Table */}
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Examination Results
                </Typography>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : results.length === 0 ? (
                  <Alert severity="info">
                    No results available. Please generate results first by clicking "Generate All Results" button above.
                  </Alert>
                ) : (
                  <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 48 }}><strong>#</strong></TableCell>
                          <TableCell><strong>Form Number</strong></TableCell>
                          <TableCell><strong>Name</strong></TableCell>
                          {isSuperAdmin && <TableCell><strong>Branch</strong></TableCell>}
                          <TableCell><strong>Course</strong></TableCell>
                          <TableCell align="center"><strong>Passed Subjects</strong></TableCell>
                          <TableCell align="center"><strong>Failed Subjects</strong></TableCell>
                          <TableCell><strong>Percentage</strong></TableCell>
                          <TableCell><strong>Grade</strong></TableCell>
                          <TableCell><strong>Result</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Details</strong></TableCell>
                          {isSuperAdmin && <TableCell><strong>Action</strong></TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {results
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((result, idx) => {
                            const passedSubjects = result.subjectResults?.filter(sr => sr.isPassed).length || 0;
                            const failedSubjects = result.subjectResults?.filter(sr => !sr.isPassed).length || 0;
                            const serialNo = page * rowsPerPage + idx + 1;

                            return (
                              <TableRow key={result._id}>
                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{serialNo}</TableCell>
                                <TableCell>{result.student?.formNumber || 'N/A'}</TableCell>
                                <TableCell>{result.student?.personalInfo?.nameAsPerTC || 'N/A'}</TableCell>
                                {isSuperAdmin && <TableCell>{result.studentExam?.branch?.name || 'N/A'}</TableCell>}
                                <TableCell>{result.student?.course?.name || 'N/A'}</TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={passedSubjects}
                                    size="small"
                                    color="success"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={failedSubjects}
                                    size="small"
                                    color="error"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell>{result.percentage}%</TableCell>
                                <TableCell>
                                  <Chip label={result.grade} size="small" color="primary" />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={result.overallResult.toUpperCase()}
                                    size="small"
                                    color={result.overallResult === 'pass' ? 'success' : 'error'}
                                  />
                                </TableCell>
                                <TableCell>
                                  {result.isPublished ? (
                                    <Chip label="Published" size="small" color="success" />
                                  ) : (
                                    <Chip label="Unpublished" size="small" color="default" />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Visibility />}
                                    onClick={() => setDetailsResult(result)}
                                  >
                                    View
                                  </Button>
                                </TableCell>
                                {isSuperAdmin && (
                                  <TableCell>
                                    {!result.isPublished && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        onClick={() => handlePublishResult(result._id)}
                                      >
                                        Publish
                                      </Button>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={results.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                  </>
                )}
              </Paper>
            </>
          )}
        </>
      )}

      {/* Subject Details Dialog */}
      <Dialog open={Boolean(detailsResult)} onClose={() => setDetailsResult(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Subject-wise Result
            </Typography>
            {detailsResult && (
              <Typography variant="body2" color="text.secondary">
                {detailsResult.student?.personalInfo?.nameAsPerTC} &nbsp;|&nbsp; {detailsResult.student?.formNumber}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailsResult && (
            <>
              {/* Summary row */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {detailsResult.percentage}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Overall %</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'grey.100', borderRadius: 2, border: '1px solid', borderColor: 'grey.300' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {detailsResult.totalMarksObtained} / {detailsResult.totalMaxMarks}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Total Marks</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'grey.100', borderRadius: 2, border: '1px solid', borderColor: 'grey.300' }}>
                    <Chip label={detailsResult.grade} color="primary" sx={{ fontSize: '1.1rem', fontWeight: 700, height: 36, px: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>Grade</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, border: '1px solid', borderColor: detailsResult.overallResult === 'pass' ? 'success.300' : 'error.300', bgcolor: detailsResult.overallResult === 'pass' ? 'success.50' : 'error.50' }}>
                    <Chip
                      label={detailsResult.overallResult.toUpperCase()}
                      color={detailsResult.overallResult === 'pass' ? 'success' : 'error'}
                      sx={{ fontSize: '1.1rem', fontWeight: 700, height: 36, px: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>Result</Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Subject breakdown table */}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Subject</strong></TableCell>
                      <TableCell align="center"><strong>Marks Obtained</strong></TableCell>
                      <TableCell align="center"><strong>Total Marks</strong></TableCell>
                      <TableCell align="center"><strong>Passing Marks</strong></TableCell>
                      <TableCell align="center"><strong>Percentage</strong></TableCell>
                      <TableCell align="center"><strong>Correct</strong></TableCell>
                      <TableCell align="center"><strong>Incorrect</strong></TableCell>
                      <TableCell align="center"><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detailsResult.subjectResults || []).map((sr, idx) => {
                      const pct = sr.totalMarks > 0 ? Math.round((sr.marksObtained / sr.totalMarks) * 100) : 0;
                      return (
                        <TableRow key={idx} sx={{ bgcolor: sr.isPassed ? 'success.50' : 'error.50' }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {sr.subject?.name || 'N/A'}
                            </Typography>
                            {sr.subject?.code && (
                              <Typography variant="caption" color="text.secondary">{sr.subject.code}</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 700, color: sr.isPassed ? 'success.main' : 'error.main' }}>
                              {sr.marksObtained}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{sr.totalMarks}</TableCell>
                          <TableCell align="center">{sr.passingMarks}</TableCell>
                          <TableCell align="center">{pct}%</TableCell>
                          <TableCell align="center">
                            <Chip label={sr.correctAnswers} size="small" color="success" variant="outlined" />
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={sr.incorrectAnswers} size="small" color="error" variant="outlined" />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={sr.isPassed ? 'PASS' : 'FAIL'}
                              size="small"
                              color={sr.isPassed ? 'success' : 'error'}
                              icon={sr.isPassed ? <CheckCircle /> : <Cancel />}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsResult(null)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Publish All Confirmation Dialog */}
      <Dialog open={showPublishDialog} onClose={() => setShowPublishDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Publish All Results</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will publish all unpublished results and send email notifications to students.
          </Alert>
          <Typography variant="body2">
            Are you sure you want to publish {resultStats?.unpublished || 0} unpublished results?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPublishDialog(false)} disabled={publishingAll}>
            Cancel
          </Button>
          <Button
            onClick={handlePublishAllResults}
            variant="contained"
            color="success"
            disabled={publishingAll}
          >
            {publishingAll ? <CircularProgress size={24} /> : 'Publish All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExamResults;
